// src/components/AccountSettings/AccountSettings.tsx

import React, { useState } from 'react';
import { useFetchUser, useFetchUsers } from '../../hooks/useFetchUsers';
import {
  Dialog,
  DialogContent,
  useTheme,
  alpha,
  Tooltip,
  Typography,
  Button,
  Paper,
  Box,
} from '@mui/material';
import { useCreateUserMutation, useUpdateUserMutation } from '../../hooks/useCreateUserMutation';
import { UB_AccountUser, UB_User } from '../../api/protosCompiled/ub_users/ub_users_pb';
import { UserForm } from './UserForm';
import { EmailService } from '../../api/emailService';
import { useFetchRoles } from '../../hooks/useFetchRoles';
// import { PermissionsChart } from './PermissionsChart';
import { isFieldDisabled } from './permissions';
import { useFetchAccount } from '../../hooks/useFetchAccount';
import { GetAccountResponse } from '../../api/protosCompiled/ub_account/ub_account_pb';
import { emailTemplates } from '../../utils/emailTemplates';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';

type CombinedField =
  | { object: 'accountUser'; field: keyof UB_AccountUser.AsObject }
  | { object: 'user'; field: keyof UB_User.AsObject };

export const AccountSettings: React.FC = () => {
  const theme = useTheme();
  const [error, setError] = useState<string | null>(null);
  const { data: users, isLoading, error: fetchUserError } = useFetchUsers();
  const { data: currentLoggedInUser } = useFetchUser();
  const { data: roles } = useFetchRoles();
  const { data: ubAccount } = useFetchAccount();
  const ubaccountid = currentLoggedInUser?.ubaccountid || '';
  const fromEmail = process.env.REACT_APP_FROM_EMAIL || '';

  // Dialog State
  const [showDialog, setShowDialog] = useState(false);
  const [dialogTitle, setDialogTitle] = useState('Add New User');
  const [isEditMode, setIsEditMode] = useState(false);
  const [tempPassword, setTempPassword] = useState('');

  // Current user in the sense of the user we are currently creating/editing
  const [currentUserData, setCurrentUserData] = useState<Partial<UB_AccountUser.AsObject>>({});

  const createUserMutation = useCreateUserMutation();
  const updateUserMutation = useUpdateUserMutation();

  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText('help@unitybuildestimation.com');
    setCopied(true);
    setTimeout(() => setCopied(false), 2000); // Hide the message after 2 seconds
  };

  // Generates a string of random characters for a temporary password for the user
  const generateTempPassword = () => {
    const randomString = Math.random().toString(36).substring(2, 8);
    setTempPassword(randomString);
    return randomString;
  };

  const handleOpenDialogForCreateUser = () => {
    setError(null); // Clear previous errors
    setIsEditMode(false);
    setDialogTitle('Create New User');
    setCurrentUserData({
      user: {
        id: '',
        name: '',
        email: '',
        userphotourl: '',
      },
      ubaccountid: ubaccountid,
      roleid: roles ? roles[1].id : '',
      isprimaryadmin: false,
      isloggedin: false,
      password: generateTempPassword(), //users to set their own passwords post log in
      isactive: true,
    });
    setShowDialog(true);
  };

  const handleOpenDialogForUpdateUser = (user: UB_AccountUser.AsObject) => {
    setError(null); // Clear previous errors
    if (!user) return;
    setIsEditMode(true);
    setDialogTitle('Edit User');
    setCurrentUserData({
      user: {
        id: user.user!.id,
        name: user.user!.name,
        email: user.user!.email,
        userphotourl: user.user!.userphotourl,
      },
      ubaccountid: ubaccountid,
      roleid: user.roleid,
      isprimaryadmin: user.isprimaryadmin,
      isloggedin: user.isloggedin,
      //password is not exposed, we handle in the backend
      isactive: user.isactive,
    });
    setShowDialog(true);
  };

  const handleDialogClose = () => {
    setShowDialog(false);
  };

  const handleUserChange = (combinedField: CombinedField, value: string | boolean) => {
    setCurrentUserData(prev => {
      if (combinedField.object === 'user' && prev.user) {
        return {
          ...prev,
          user: {
            ...prev.user,
            [combinedField.field]: value,
          },
        };
      }
      return {
        ...prev,
        [combinedField.field]: value,
      };
    });
  };

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Remove password from `currentUserData` if it's blank
    const updatedUserData = { ...currentUserData };
    if (!updatedUserData.password) {
      delete updatedUserData.password; // Prevent sending an empty password
    }
    if (isEditMode) {
      // Update existing user
      updateUserMutation.mutate(currentUserData as UB_AccountUser.AsObject, {
        onSuccess: async () => {
          // Send email after successful update
          if (currentUserData.user!.email) {
            const template = emailTemplates.accountUpdate(currentUserData.user!.name, fromEmail);
            await EmailService.sendEmail({
              from: fromEmail,
              toList: [currentUserData.user!.email],
              subject: template.subject,
              plaintextcontent: template.plaintext,
              htmlcontent: template.html,
            });
          }
          handleDialogClose();
        },
        onError: (err: any) => {
          // Catch server-side error and store it in local state
          console.error('Update user error:', err);
          setError(err.message || 'Failed to update user. Please try again.');
        },
      });
    } else {
      // Create a new user
      createUserMutation.mutate(currentUserData as UB_AccountUser.AsObject, {
        onSuccess: async () => {
          // Send email after successful creation
          if (currentUserData.user!.email) {
            const template = emailTemplates.newUser(
              currentUserData.user!.name,
              currentUserData.user!.email,
              tempPassword,
              fromEmail
            );
            await EmailService.sendEmail({
              from: fromEmail,
              toList: [currentUserData.user!.email],
              subject: template.subject,
              plaintextcontent: template.plaintext,
              htmlcontent: template.html,
            });
          }
          handleDialogClose();
        },
        onError: (err: any) => {
          // Catch server-side error and store it in local state
          console.error('Create user error:', err);
          setError(err.message || 'Failed to create user. Please try again.');
        },
      });
    }
  };

  if (isLoading) {
    return <Typography>Loading account settings...</Typography>;
  }

  if (fetchUserError) {
    return <Typography color="error">Error loading users data.</Typography>;
  }

  const numAvailableSeats = (ubAccount: GetAccountResponse.AsObject | undefined) => {
    if (!ubAccount) return 0; // Return a default value if ubAccount is undefined
    const loggedInUsersCount = ubAccount.usersList?.filter(user => user.isloggedin).length || 0;
    const totalUsers = ubAccount.account?.numusers || 0;
    return totalUsers - loggedInUsersCount;
  };

  return (
    <>
      <Box
        sx={{
          padding: '20px',
          color: theme.palette.text.primary,
          backgroundColor: theme.palette.background.default,
        }}
      >
        <Typography variant="h4" component="h2">
          {ubAccount?.account?.accountname}'s Account Settings
        </Typography>
        <Typography variant="h6" component="h4">
          User: {currentLoggedInUser?.user?.name}
        </Typography>
        {/* <PermissionsChart /> */}

        <Box sx={{ marginBottom: '20px', mt: 2 }}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleOpenDialogForCreateUser}
            disabled={isFieldDisabled(currentLoggedInUser, undefined, 'Add New User')}
            startIcon={<AddIcon aria-hidden="true" />}
            aria-label="Add new user"
            sx={{ mb: 2 }}
          >
            Add New User
          </Button>

          {currentLoggedInUser?.isprimaryadmin && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body1">
                To make account changes, please email{' '}
                <Tooltip title="Click to copy email address" arrow>
                  <Box
                    component="span"
                    onClick={handleCopy}
                    sx={{
                      cursor: 'pointer',
                      color: theme.palette.primary.main,
                      textDecoration: 'underline',
                      display: 'inline-flex',
                      alignItems: 'center',
                      '&:hover': {
                        color: theme.palette.primary.dark,
                      },
                    }}
                    role="button"
                    tabIndex={0}
                    aria-label="Copy help email to clipboard"
                    onKeyDown={e => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        handleCopy();
                      }
                    }}
                  >
                    help@unitybuildestimation.com
                    <ContentCopyIcon fontSize="small" sx={{ ml: 0.5 }} aria-hidden="true" />
                  </Box>
                </Tooltip>
                and we will be happy to help you.
                {copied && (
                  <Typography component="span" sx={{ ml: 1, color: theme.palette.success.main }}>
                    Copied to clipboard!
                  </Typography>
                )}
              </Typography>
            </Box>
          )}

          <Typography>
            <strong>Current Seat Count:</strong> {ubAccount?.account?.numusers}
          </Typography>
          <Typography>
            <strong>Current Available Seats:</strong> {numAvailableSeats(ubAccount as any)}
          </Typography>
        </Box>

        <Paper
          elevation={1}
          sx={{
            backgroundColor: theme.palette.background.paper,
            overflow: 'auto',
            width: '100%',
          }}
        >
          <Box
            component="table"
            sx={{
              width: '100%',
              borderCollapse: 'collapse',
              tableLayout: 'auto',
              '& th': {
                backgroundColor: theme.palette.mode === 'dark' ? '#2c2c2c' : '#f5f5f5',
                color: theme.palette.text.primary,
                textAlign: 'left',
                padding: '12px 8px',
                whiteSpace: 'nowrap',
                fontWeight: 600,
              },
              '& td': {
                padding: '12px 8px',
                color: theme.palette.text.primary,
              },
              '& tr:nth-of-type(odd)': {
                backgroundColor:
                  theme.palette.mode === 'dark' ? '#2c2c2c !important' : '#f5f5f5 !important',
              },
              '& tr:nth-of-type(even)': {
                backgroundColor:
                  theme.palette.mode === 'dark' ? '#3c3c3c !important' : '#e0e0e0 !important',
              },
              '& tr:hover': {
                backgroundColor: `${alpha(theme.palette.primary.main, 0.1)} !important`,
              },
            }}
            role="table"
            aria-label="User account list"
          >
            <thead>
              <tr>
                <th scope="col">Name</th>
                <th scope="col">Email</th>
                <th scope="col">User Level</th>
                <th scope="col">Account Admin</th>
                <th scope="col">Logged In</th>
                <th scope="col">Is Active</th>
                <th scope="col">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users &&
                users.map(user => (
                  <tr key={user.user?.id}>
                    <td>{user.user?.name}</td>
                    <td>{user.user?.email}</td>
                    <td>{user.rolename}</td>
                    <td>{user.isprimaryadmin ? 'Yes' : 'No'}</td>
                    <td>{user.isloggedin ? 'Yes' : 'No'}</td>
                    <td>{user.isactive ? 'Yes' : 'No'}</td>
                    <td>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => handleOpenDialogForUpdateUser(user)}
                        startIcon={<EditIcon aria-hidden="true" />}
                        aria-label={`Edit user ${user.user?.name}`}
                      >
                        Edit
                      </Button>
                    </td>
                  </tr>
                ))}
            </tbody>
          </Box>
        </Paper>
      </Box>

      {/* User Form Dialog */}
      <Dialog
        open={showDialog}
        onClose={handleDialogClose}
        aria-labelledby="user-form-title"
        aria-describedby="user-form-description"
      >
        <DialogContent
          sx={{
            backgroundColor: theme.palette.background.paper,
            color: theme.palette.text.primary,
          }}
        >
          <UserForm
            userData={currentUserData}
            onChange={handleUserChange as any}
            onSubmit={handleFormSubmit}
            onCancel={handleDialogClose}
            title={dialogTitle}
            currentLoggedInUser={currentLoggedInUser}
            error={error}
          />
        </DialogContent>
      </Dialog>
    </>
  );
};
